import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import usePageMetadata from '../../hooks/usePageMetadata';
import { findMaterials } from '../../services/materialService';
import { MATERIAL_MECHANICAL_PROPERTY_SCALES } from './materialMechanicalProperty';
import {
  buildFinderCriteria, CHEMICAL_ELEMENT_NAMES, CHEMICAL_ELEMENTS, createFinderForm, hasFinderCriterion, validateFinderForm,
} from './materialFinderForm';
import { getMaterialComparisonUrl } from './materialTools';
import './MaterialFinder.css';
import { useUnits } from '../../units/UnitContext';
import { formatUnitValue, unitLabel } from '../../units/unitConversions';
import { useUnitDrafts } from '../../units/useUnitDrafts';
const formatFinderNumber = value => new Intl.NumberFormat('en-US', { maximumFractionDigits: 4 }).format(value);

const formatCandidateValue = (candidate, unitSystem) => {
  if (!candidate) return '—';
  const isStrength = candidate.scale === 'MPa_Rm';
  const number = v => isStrength && unitSystem === 'inch' ? formatUnitValue(v, 'mpa', unitSystem) : formatFinderNumber(v);
  const value = candidate.min === candidate.max ? number(candidate.min) : `${number(candidate.min)}–${number(candidate.max)}`;
  return candidate.unit ? `${value} ${isStrength ? unitLabel('mpa', unitSystem) : candidate.unit}` : value;
};


const STATUS_LABELS = { match: 'Match', close: 'Close', partial: 'Partial', poor: 'Poor', unknown: 'Unknown' };

const RangeFields = ({ id, value, onChange, unit }) => (
  <div className="materialFinderRange">
    <label htmlFor={`${id}-min`}>From
      <input id={`${id}-min`} type="number" min="0" step="any" value={value.min}
        onChange={event => onChange('min', event.target.value)} />
    </label>
    <span aria-hidden="true">–</span>
    <label htmlFor={`${id}-max`}>To
      <input id={`${id}-max`} type="number" min="0" step="any" value={value.max}
        onChange={event => onChange('max', event.target.value)} />
    </label>
    {unit && <span className="materialFinderUnit">{unit}</span>}
  </div>
);

const MaterialFinder = () => {
  usePageMetadata('Material Finder | ASiJoLab',
    'Find the closest published ASiJoLab materials from strength, hardness, thermal, elongation or chemistry ranges.');
  const { unitSystem } = useUnits();
  const [form, setForm] = useState(createFinderForm);
  const canonicalFinder = useUnitDrafts(form, setForm, form.mechanicalProperty.scale === 'MPa_Rm' ? { 'mechanicalProperty.min': 'mpa', 'mechanicalProperty.max': 'mpa' } : {}, unitSystem);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const validation = useMemo(() => validateFinderForm(form), [form]);
  const hasCriterion = hasFinderCriterion(form);

  const updateRange = (category, bound, value) => setForm(current => ({
    ...current, [category]: { ...current[category], [bound]: value },
  }));
  const addChemistry = () => setForm(current => {
    const element = CHEMICAL_ELEMENTS.find(option => !current.chemistry.some(row => row.element === option));
    return element ? { ...current, chemistry: [...current.chemistry, { element, min: '', max: '' }] } : current;
  });
  const updateChemistry = (index, field, value) => setForm(current => ({
    ...current,
    chemistry: current.chemistry.map((row, rowIndex) => rowIndex === index ? { ...row, [field]: value } : row),
  }));
  const removeChemistry = index => setForm(current => ({
    ...current, chemistry: current.chemistry.filter((_, rowIndex) => rowIndex !== index),
  }));

  const submit = async event => {
    event.preventDefault();
    if (loading) return;
    setError('');
    try {
      const criteria = buildFinderCriteria(form);
      if (criteria.mechanicalProperty?.scale === 'MPa_Rm' && unitSystem === 'inch') {
        for (const bound of ['min', 'max']) if (criteria.mechanicalProperty[bound] !== undefined) criteria.mechanicalProperty[bound] = canonicalFinder(`mechanicalProperty.${bound}`, form.mechanicalProperty[bound], 'mpa');
      }
      setLoading(true);
      const response = await findMaterials(criteria);
      setResults(response.results);
    } catch (requestError) {
      setError(requestError?.response?.data?.message || requestError?.message || 'Material Finder could not complete the search.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="materialFinder">
      <header className="materialFinderIntro">
        <p className="materialFinderEyebrow">Materials database</p>
        <h1>Material Finder</h1>
        <p>Enter anything you know about the material. ASiJoLab will find the closest matches in the material database.</p>
      </header>

      <form className="materialFinderForm" onSubmit={submit}>
        <fieldset>
          <legend>Mechanical strength / hardness {form.mechanicalProperty.scale === 'MPa_Rm' ? `(${unitLabel('mpa', unitSystem)})` : ''}</legend>
          <RangeFields id="finder-mechanical" value={form.mechanicalProperty}
            onChange={(bound, value) => updateRange('mechanicalProperty', bound, value)} />
          <label className="materialFinderScale" htmlFor="finder-mechanical-scale">Scale
            <select id="finder-mechanical-scale" value={form.mechanicalProperty.scale}
              onChange={event => updateRange('mechanicalProperty', 'scale', event.target.value)}>
              {MATERIAL_MECHANICAL_PROPERTY_SCALES.map(option =>
                <option key={option.value} value={option.value}>{option.value === 'MPa_Rm' ? `${unitLabel('mpa', unitSystem)} (Rm)` : option.label}</option>)}
            </select>
          </label>
        </fieldset>

        <fieldset>
          <legend>Thermal conductivity</legend>
          <RangeFields id="finder-thermal" value={form.thermalConductivity}
            onChange={(bound, value) => updateRange('thermalConductivity', bound, value)} unit="W/m·K" />
        </fieldset>

        <fieldset>
          <legend>Elongation</legend>
          <RangeFields id="finder-elongation" value={form.elongation}
            onChange={(bound, value) => updateRange('elongation', bound, value)} unit="%" />
        </fieldset>

        <fieldset>
          <legend>Chemical composition</legend>
          <div className="materialFinderChemistry">
            {form.chemistry.map((row, index) => (
              <div className="materialFinderChemicalRow" key={`${row.element}-${index}`}>
                <label htmlFor={`finder-element-${index}`}>Element
                  <select id={`finder-element-${index}`} value={row.element}
                    onChange={event => updateChemistry(index, 'element', event.target.value)}>
                    {CHEMICAL_ELEMENTS.map(element => <option key={element} value={element}
                      disabled={form.chemistry.some((other, otherIndex) => otherIndex !== index && other.element === element)}>
                      {element} — {CHEMICAL_ELEMENT_NAMES[element]}
                    </option>)}
                  </select>
                </label>
                <RangeFields id={`finder-chemistry-${index}`} value={row}
                  onChange={(bound, value) => updateChemistry(index, bound, value)} unit="%" />
                <button type="button" className="materialFinderRemove" onClick={() => removeChemistry(index)}
                  aria-label={`Remove ${row.element} criterion`}>Remove</button>
              </div>
            ))}
          </div>
          <button type="button" className="materialFinderAdd" onClick={addChemistry}
            disabled={form.chemistry.length >= CHEMICAL_ELEMENTS.length}>+ Add element</button>
        </fieldset>

        {hasCriterion && validation && <p className="materialFinderError" role="alert">{validation}</p>}
        {error && <p className="materialFinderError" role="alert">{error}</p>}
        <button className="materialFinderSubmit" type="submit" disabled={!hasCriterion || !!validation || loading}>
          {loading ? 'Finding materials…' : 'Find materials'}
        </button>
      </form>

      {results && <section className="materialFinderResults" aria-live="polite">
        <h2>Closest matches</h2>
        {results.length === 0 ? <div className="materialFinderEmpty">
          <p>No sufficiently close materials were found.</p><p>Try widening one or more ranges.</p>
        </div> : results.map(result => <article
          className={`materialFinderCard${result.score === 100 ? ' materialFinderCardPerfect' : ''}`} key={result.slug}>
          <h3>{result.materialName}</h3>
          {result.otherNames.length > 0 && <p>{result.otherNames.join(', ')}</p>}
          <div className="materialFinderScores">
            <strong className={result.score === 100 ? 'materialFinderPerfectScore' : ''}>Match: {result.score}%</strong>
            <span>Coverage: {result.coverage}%</span>
          </div>
          <dl>{result.breakdown.map(item => <div key={`${item.category}-${item.criterion}`}>
            <dt>{item.criterion}</dt>
            <dd className={`materialFinderStatus is-${item.status}`}>{STATUS_LABELS[item.status]}</dd>
            <dd className="materialFinderCandidateValue">{formatCandidateValue(item.candidate, unitSystem)}</dd>
          </div>)}</dl>
          <div className="materialFinderActions">
            <Link className="materialFinderView" to={`/materials/${encodeURIComponent(result.slug)}`}>View material</Link>
            <Link className="materialFinderCompare" to={getMaterialComparisonUrl('materialB', result.slug)}>Compare</Link>
          </div>
        </article>)}
      </section>}
    </main>
  );
};

export default MaterialFinder;
